import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { altioraToast } from "@/components/altiora/toast";
import { PageShell } from "@/components/altiora/PremiumUI";
import {
  Mic, Square, Play, Loader2, ArrowRight, RotateCcw, CheckCircle2,
  Sparkles, Trophy, AlertTriangle, Clock, Type, Trash2, ChevronRight,
} from "lucide-react";

// ---------------------------------------------------------------------------
// Voice recorder hook — MediaRecorder API → webm/opus blob
// ---------------------------------------------------------------------------
function useVoiceRecorder() {
  const [state, setState] = useState("idle"); // idle | recording | stopped
  const [elapsed, setElapsed] = useState(0);
  const [blob, setBlob] = useState(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const startedAtRef = useRef(0);
  const tickRef = useRef(null);

  const cleanup = () => {
    if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
    if (recorderRef.current?.stream) {
      recorderRef.current.stream.getTracks().forEach((t) => t.stop());
    }
    recorderRef.current = null;
    chunksRef.current = [];
  };

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "";
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : {});
      recorderRef.current = rec;
      chunksRef.current = [];
      rec.ondataavailable = (e) => e.data.size > 0 && chunksRef.current.push(e.data);
      rec.onstop = () => {
        const b = new Blob(chunksRef.current, { type: mime || "audio/webm" });
        setBlob(b);
        setState("stopped");
        cleanup();
      };
      rec.start();
      startedAtRef.current = Date.now();
      setElapsed(0);
      setBlob(null);
      setState("recording");
      tickRef.current = setInterval(() => setElapsed(Math.floor((Date.now() - startedAtRef.current) / 1000)), 250);
    } catch (e) {
      console.warn("Microphone unavailable:", e);
      altioraToast.error("Micro indisponible — accordez la permission dans le navigateur.");
      setState("idle");
    }
  };

  const stop = () => {
    if (recorderRef.current && state === "recording") {
      recorderRef.current.stop();
    }
  };

  const reset = () => {
    cleanup();
    setBlob(null);
    setElapsed(0);
    setState("idle");
  };

  useEffect(() => () => cleanup(), []);
  return { state, elapsed, blob, start, stop, reset };
}

const SENIORITY = [
  { key: "junior", fr: "Junior (0-2 ans)" },
  { key: "intermediate", fr: "Intermédiaire (2-5 ans)" },
  { key: "senior", fr: "Senior (5-10 ans)" },
  { key: "lead", fr: "Lead / Manager (10+)" },
];
const DIFFICULTY = [
  { key: "easy", fr: "Souple · 4 questions" },
  { key: "medium", fr: "Réel · 6 questions" },
  { key: "hard", fr: "Exigeant · 8 questions" },
];

export default function Interview() {
  const [screen, setScreen] = useState("setup"); // setup | live | report
  const [creating, setCreating] = useState(false);
  const [session, setSession] = useState(null);
  const [history, setHistory] = useState([]);
  const [current, setCurrent] = useState(0); // index in questions
  const [textAnswer, setTextAnswer] = useState("");
  const [mode, setMode] = useState("voice"); // voice | text
  const [submitting, setSubmitting] = useState(false);
  const [finishing, setFinishing] = useState(false);
  const [showAnswerDetail, setShowAnswerDetail] = useState(null);
  const recorder = useVoiceRecorder();

  const [setup, setSetup] = useState({
    role: "",
    sector: "",
    seniority: "intermediate",
    language: "fr",
    difficulty: "medium",
    context: "",
  });

  useEffect(() => { loadHistory(); }, []);

  const loadHistory = async () => {
    try {
      const r = await api.get("/interview/sessions?limit=20");
      setHistory(r.data.sessions || []);
    } catch { /* ignore */ }
  };

  const startSession = async () => {
    if (!setup.role || setup.role.length < 2) {
      altioraToast.error("Précisez le rôle visé.");
      return;
    }
    setCreating(true);
    try {
      const r = await api.post("/interview/sessions", setup);
      setSession(r.data.session);
      setCurrent(0);
      setTextAnswer("");
      recorder.reset();
      setScreen("live");
      altioraToast.success(`${r.data.session.questions.length} questions générées`);
    } catch (e) {
      altioraToast.error(e.response?.data?.detail || "Impossible de démarrer la simulation");
    } finally {
      setCreating(false);
    }
  };

  const currentQ = session?.questions?.[current];
  const currentAnswer = session?.answers?.find((a) => a.question_idx === currentQ?.idx);

  const submitAnswer = async () => {
    if (!session || !currentQ) return;
    setSubmitting(true);
    try {
      const form = new FormData();
      form.append("question_idx", String(currentQ.idx));
      if (mode === "voice") {
        if (!recorder.blob) { altioraToast.error("Enregistrez votre réponse d'abord."); setSubmitting(false); return; }
        form.append("audio", recorder.blob, "answer.webm");
      } else {
        if (textAnswer.trim().length < 10) { altioraToast.error("Réponse trop courte (min 10 caractères)."); setSubmitting(false); return; }
        form.append("answer_text", textAnswer.trim());
      }
      const r = await api.post(`/interview/sessions/${session.id}/answer`, form, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 90000,
      });
      // refresh session with new answer
      const s = await api.get(`/interview/sessions/${session.id}`);
      setSession(s.data.session);
      setShowAnswerDetail(r.data.answer);
      altioraToast.success(`Score ${r.data.answer.eval.score}/100`);
    } catch (e) {
      altioraToast.error(e.response?.data?.detail || "Évaluation impossible");
    } finally {
      setSubmitting(false);
    }
  };

  const next = () => {
    if (current + 1 < (session?.questions?.length || 0)) {
      setCurrent(current + 1);
      setTextAnswer("");
      recorder.reset();
      setShowAnswerDetail(null);
    }
  };

  const finish = async () => {
    if (!session) return;
    setFinishing(true);
    try {
      const r = await api.post(`/interview/sessions/${session.id}/finish`);
      setSession(r.data.session);
      setScreen("report");
      loadHistory();
    } catch (e) {
      altioraToast.error(e.response?.data?.detail || "Finalisation impossible");
    } finally {
      setFinishing(false);
    }
  };

  const backToSetup = () => {
    setScreen("setup");
    setSession(null);
    setCurrent(0);
    recorder.reset();
    setTextAnswer("");
    loadHistory();
  };

  const answeredCount = session?.answers?.length || 0;
  const totalQ = session?.questions?.length || 0;
  const progress = totalQ ? Math.round((answeredCount / totalQ) * 100) : 0;
  const allAnswered = totalQ > 0 && answeredCount === totalQ;

  return (
    <PageShell testid="interview-page">
      {/* TopBar rendered globally by AppShell */}
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-10 md:py-14">
        {screen === "setup" && (
          <SetupScreen
            setup={setup} setSetup={setSetup}
            history={history}
            onStart={startSession}
            onOpenReport={(id) => { setSession(null); openHistoricalSession(id, setSession, setScreen); }}
            creating={creating}
          />
        )}

        {screen === "live" && session && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left: current question + input */}
            <div className="lg:col-span-2 space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#5B5B62]">
                    Simulation · {session.role} · {session.difficulty}
                  </div>
                  <div className="text-sm text-[#8B8B8F] mt-0.5">Question {current + 1} / {totalQ}</div>
                </div>
                <button
                  onClick={backToSetup}
                  className="text-xs font-mono uppercase tracking-widest text-[#5B5B62] hover:text-[#DC2626]"
                  data-testid="interview-abort-btn"
                >
                  Abandonner
                </button>
              </div>

              <div className="h-1 rounded bg-[#EBE8DE] overflow-hidden">
                <div className="h-full transition-all" style={{ width: `${progress}%`, background: "linear-gradient(90deg,#0B2545,#123163)" }} />
              </div>

              <div className="rounded-xl border border-[#E4E1D8] bg-white p-6" data-testid="interview-current-question">
                <div className="inline-flex items-center gap-2 rounded-full border border-[#C5A880]/40 bg-[#C5A880]/12 px-2 py-0.5 mb-3">
                  <span className="font-mono text-[9px] uppercase tracking-[0.22em] text-[#7A5B14]">
                    {currentQ?.type} · {currentQ?.timing_seconds}s
                  </span>
                </div>
                <div className="font-display text-xl md:text-2xl text-[#1F2937] leading-snug">{currentQ?.question_fr}</div>
                {currentQ?.expected_focus && (
                  <div className="mt-3 text-xs text-[#8B8B8F] italic">Attendu : {currentQ.expected_focus}</div>
                )}
              </div>

              {!currentAnswer ? (
                <>
                  {/* Mode switch */}
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setMode("voice")}
                      data-testid="interview-mode-voice"
                      className={`text-xs font-mono uppercase tracking-widest px-3 py-1.5 rounded-md border ${mode === "voice" ? "bg-[#0B2545] text-white border-[#0B2545]" : "border-[#E4E1D8] text-[#5B5B62]"}`}
                    >
                      <Mic className="h-3 w-3 inline mr-1" /> Voix
                    </button>
                    <button
                      onClick={() => setMode("text")}
                      data-testid="interview-mode-text"
                      className={`text-xs font-mono uppercase tracking-widest px-3 py-1.5 rounded-md border ${mode === "text" ? "bg-[#0B2545] text-white border-[#0B2545]" : "border-[#E4E1D8] text-[#5B5B62]"}`}
                    >
                      <Type className="h-3 w-3 inline mr-1" /> Texte
                    </button>
                  </div>

                  {mode === "voice" ? (
                    <VoicePanel recorder={recorder} />
                  ) : (
                    <textarea
                      data-testid="interview-text-input"
                      value={textAnswer}
                      onChange={(e) => setTextAnswer(e.target.value)}
                      placeholder="Répondez à la question, structurez par STAR si demandé (Situation, Tâche, Action, Résultat)…"
                      className="w-full min-h-[180px] rounded-lg border border-[#E4E1D8] bg-white p-4 text-sm text-[#1F2937] focus:outline-none focus:border-[#0B2545]"
                    />
                  )}

                  <div className="flex items-center justify-end gap-2">
                    <button
                      onClick={submitAnswer}
                      disabled={submitting || (mode === "voice" && !recorder.blob) || (mode === "text" && textAnswer.trim().length < 10)}
                      data-testid="interview-submit-answer"
                      className="inline-flex items-center gap-2 rounded-md bg-[#0B2545] hover:bg-[#123163] disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-medium px-5 py-2.5"
                    >
                      {submitting ? <><Loader2 className="h-4 w-4 animate-spin" /> Évaluation…</> : <>Soumettre <ArrowRight className="h-4 w-4" /></>}
                    </button>
                  </div>
                </>
              ) : (
                <AnsweredCard answer={currentAnswer} onNext={next} isLast={current + 1 >= totalQ} onFinish={finish} finishing={finishing} allAnswered={allAnswered} />
              )}
            </div>

            {/* Right: question list */}
            <div className="rounded-xl border border-[#E4E1D8] bg-white p-4 h-fit lg:sticky lg:top-4" data-testid="interview-question-list">
              <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#5B5B62] mb-3">Questions</div>
              <ol className="space-y-1.5">
                {session.questions.map((q, i) => {
                  const done = session.answers?.some((a) => a.question_idx === q.idx);
                  const isActive = i === current;
                  return (
                    <li key={q.idx}>
                      <button
                        onClick={() => setCurrent(i)}
                        data-testid={`interview-goto-q-${q.idx}`}
                        className={`w-full text-left rounded-md px-3 py-2 text-xs flex items-start gap-2 ${
                          isActive ? "bg-[#0B2545]/8 border border-[#0B2545]/25" : "hover:bg-[#F1EFE9] border border-transparent"
                        }`}
                      >
                        <span className={`inline-flex items-center justify-center h-5 w-5 rounded-full text-[10px] font-mono flex-shrink-0 ${done ? "bg-[#059669] text-white" : "bg-[#E4E1D8] text-[#5B5B62]"}`}>
                          {done ? "✓" : q.idx}
                        </span>
                        <span className="flex-1 min-w-0 text-[#1F2937] leading-snug line-clamp-2">{q.question_fr}</span>
                      </button>
                    </li>
                  );
                })}
              </ol>
              {allAnswered && (
                <button
                  onClick={finish}
                  disabled={finishing}
                  data-testid="interview-finish-btn"
                  className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-md bg-gradient-to-r from-[#C5A880] to-[#B08948] hover:opacity-90 text-white text-sm font-medium px-4 py-2.5"
                >
                  {finishing ? <><Loader2 className="h-4 w-4 animate-spin" /> Génération du rapport…</> : <><Trophy className="h-4 w-4" /> Voir mon rapport final</>}
                </button>
              )}
            </div>
          </div>
        )}

        {screen === "report" && session?.report && (
          <ReportScreen session={session} onNew={backToSetup} />
        )}
      </div>
    </PageShell>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------
function SetupScreen({ setup, setSetup, history, onStart, creating, onOpenReport }) {
  return (
    <>
      <div className="max-w-3xl">
        <div className="text-xs font-mono uppercase tracking-[0.28em] text-navy mb-3">Interview Prep Studio</div>
        <h1 className="font-display text-4xl sm:text-5xl text-navy tracking-tight leading-[1.05]">Simulez votre prochaine entrevue.</h1>
        <p className="mt-5 text-base md:text-lg text-[#374151] leading-relaxed">
          Un jury virtuel senior génère 4 à 8 questions pointues, écoute vos réponses (voix ou texte), et vous rend un feedback structuré avec une reformulation modèle pour chaque réponse.
        </p>
      </div>

      <div className="mt-8 rounded-xl border border-[#E4E1D8] bg-white p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Rôle visé *" testId="setup-role">
          <input
            data-testid="interview-setup-role"
            value={setup.role}
            onChange={(e) => setSetup({ ...setup, role: e.target.value })}
            placeholder="ex : Analyste SOC L2, Chef de produit, Data engineer…"
            className="w-full rounded-md border border-[#E4E1D8] bg-white px-3 py-2 text-sm focus:outline-none focus:border-[#0B2545]"
          />
        </Field>
        <Field label="Secteur">
          <input
            data-testid="interview-setup-sector"
            value={setup.sector}
            onChange={(e) => setSetup({ ...setup, sector: e.target.value })}
            placeholder="ex : Cybersécurité, Fintech, Santé"
            className="w-full rounded-md border border-[#E4E1D8] bg-white px-3 py-2 text-sm focus:outline-none focus:border-[#0B2545]"
          />
        </Field>
        <Field label="Séniorité">
          <select
            data-testid="interview-setup-seniority"
            value={setup.seniority}
            onChange={(e) => setSetup({ ...setup, seniority: e.target.value })}
            className="w-full rounded-md border border-[#E4E1D8] bg-white px-3 py-2 text-sm focus:outline-none focus:border-[#0B2545]"
          >
            {SENIORITY.map((s) => <option key={s.key} value={s.key}>{s.fr}</option>)}
          </select>
        </Field>
        <Field label="Difficulté">
          <select
            data-testid="interview-setup-difficulty"
            value={setup.difficulty}
            onChange={(e) => setSetup({ ...setup, difficulty: e.target.value })}
            className="w-full rounded-md border border-[#E4E1D8] bg-white px-3 py-2 text-sm focus:outline-none focus:border-[#0B2545]"
          >
            {DIFFICULTY.map((d) => <option key={d.key} value={d.key}>{d.fr}</option>)}
          </select>
        </Field>
        <div className="md:col-span-2">
          <Field label="Contexte additionnel (optionnel)">
            <textarea
              data-testid="interview-setup-context"
              value={setup.context}
              onChange={(e) => setSetup({ ...setup, context: e.target.value })}
              placeholder="ex : reconversion depuis le marketing, poste au Canada, salaire cible, spécificités du recruteur…"
              rows={3}
              className="w-full rounded-md border border-[#E4E1D8] bg-white px-3 py-2 text-sm focus:outline-none focus:border-[#0B2545]"
            />
          </Field>
        </div>
      </div>

      <div className="mt-6 flex items-center justify-between">
        <div className="text-xs text-[#8B8B8F]">
          <Sparkles className="h-3 w-3 inline mr-1 text-[#C5A880]" /> Claude Sonnet 4.6 · Whisper pour la voix · rapport en fin de session
        </div>
        <button
          onClick={onStart}
          disabled={creating}
          data-testid="interview-start-btn"
          className="inline-flex items-center gap-2 rounded-md bg-[#0B2545] hover:bg-[#123163] disabled:opacity-50 text-white text-sm font-medium px-6 py-2.5"
        >
          {creating ? <><Loader2 className="h-4 w-4 animate-spin" /> Génération des questions…</> : <>Démarrer la simulation <ArrowRight className="h-4 w-4" /></>}
        </button>
      </div>

      {history.length > 0 && (
        <div className="mt-12">
          <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#5B5B62] mb-3">Simulations précédentes</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {history.map((s) => (
              <button
                key={s.id}
                onClick={() => onOpenReport(s.id)}
                data-testid={`interview-history-${s.id}`}
                className="text-left rounded-lg border border-[#E4E1D8] bg-white p-4 hover:border-[#0B2545]/40 transition"
              >
                <div className="flex items-center justify-between">
                  <div className="text-sm font-medium text-[#1F2937] truncate">{s.role}</div>
                  {s.overall_score !== null && s.overall_score !== undefined ? (
                    <span className="text-xs font-mono text-[#7A5B14] bg-[#FBF6EB] border border-[#E7D9B3] rounded px-2 py-0.5">
                      {s.overall_score}/100
                    </span>
                  ) : (
                    <span className="text-xs font-mono text-[#5B5B62] bg-[#F1EFE9] rounded px-2 py-0.5">
                      {s.answers_total}/{s.questions_total}
                    </span>
                  )}
                </div>
                <div className="mt-1 text-[11px] text-[#8B8B8F] font-mono">
                  {s.status === "completed" ? "Terminée" : "En cours"} · {s.difficulty} · {new Date(s.created_at).toLocaleDateString()}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-[#5B5B62] mb-1.5">{label}</div>
      {children}
    </div>
  );
}

function VoicePanel({ recorder }) {
  const audioUrl = recorder.blob ? URL.createObjectURL(recorder.blob) : null;
  return (
    <div className="rounded-xl border border-[#E4E1D8] bg-white p-6">
      <div className="flex flex-col items-center text-center">
        {recorder.state === "recording" ? (
          <button
            onClick={recorder.stop}
            data-testid="interview-stop-btn"
            className="h-24 w-24 rounded-full bg-[#DC2626] text-white hover:bg-[#B01C1C] flex items-center justify-center shadow-lg animate-pulse"
          >
            <Square className="h-9 w-9" fill="currentColor" />
          </button>
        ) : recorder.state === "stopped" ? (
          <button
            onClick={recorder.reset}
            data-testid="interview-reset-btn"
            className="h-24 w-24 rounded-full bg-white border-2 border-[#E4E1D8] text-[#5B5B62] hover:border-[#0B2545] flex items-center justify-center"
            title="Recommencer"
          >
            <RotateCcw className="h-8 w-8" />
          </button>
        ) : (
          <button
            onClick={recorder.start}
            data-testid="interview-record-btn"
            className="h-24 w-24 rounded-full bg-[#0B2545] hover:bg-[#123163] text-white flex items-center justify-center shadow-lg hover:shadow-xl transition-shadow"
          >
            <Mic className="h-9 w-9" />
          </button>
        )}
        <div className="mt-4 flex items-center gap-2">
          <Clock className="h-4 w-4 text-[#5B5B62]" />
          <span className="font-mono text-lg text-[#1F2937]">{formatTime(recorder.elapsed)}</span>
        </div>
        <div className="mt-2 text-xs text-[#5B5B62]">
          {recorder.state === "idle" && "Cliquez pour parler. Structurez par STAR quand demandé."}
          {recorder.state === "recording" && "Enregistrement en cours — cliquez pour arrêter."}
          {recorder.state === "stopped" && "Enregistrement prêt. Écoutez avant de soumettre."}
        </div>
        {audioUrl && (
          <audio src={audioUrl} controls className="mt-4 w-full max-w-sm" data-testid="interview-audio-preview" />
        )}
      </div>
    </div>
  );
}

function AnsweredCard({ answer, onNext, isLast, onFinish, finishing, allAnswered }) {
  const e = answer.eval || {};
  const stars = e.star_coverage || {};
  return (
    <div className="rounded-xl border border-[#059669]/25 bg-[#F0FDF4] p-6" data-testid="interview-answered-card">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="inline-flex items-center gap-2 rounded-full border border-[#059669]/40 bg-white px-2 py-0.5 mb-2">
            <CheckCircle2 className="h-3 w-3 text-[#059669]" />
            <span className="font-mono text-[10px] uppercase tracking-widest text-[#065F46]">Réponse évaluée</span>
          </div>
          <div className="text-4xl font-display text-[#065F46] leading-none" data-testid="interview-answer-score">{e.score}<span className="text-lg text-[#059669]">/100</span></div>
        </div>
        <div className="text-right">
          <div className="text-[10px] font-mono uppercase tracking-widest text-[#5B5B62] mb-1">STAR</div>
          <div className="flex gap-1">
            {["situation", "task", "action", "result"].map((k) => (
              <span key={k} className={`h-6 w-6 rounded flex items-center justify-center text-[10px] font-mono ${stars[k] ? "bg-[#059669] text-white" : "bg-white border border-[#E4E1D8] text-[#8B8B8F]"}`} title={k}>
                {k[0].toUpperCase()}
              </span>
            ))}
          </div>
        </div>
      </div>

      {e.feedback_fr && <div className="mt-4 text-sm text-[#1F2937] italic">{e.feedback_fr}</div>}

      <details className="mt-4 group" open>
        <summary className="cursor-pointer text-xs font-mono uppercase tracking-widest text-[#5B5B62]">Détail forces / lacunes / réponse modèle</summary>
        <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <div className="text-[11px] font-mono uppercase tracking-widest text-[#059669] mb-1.5">Forces</div>
            <ul className="space-y-1.5">
              {(e.strengths_fr || []).map((s, i) => (
                <li key={i} className="text-xs text-[#1F2937] flex gap-2"><CheckCircle2 className="h-3 w-3 mt-0.5 text-[#059669] shrink-0" /><span>{s}</span></li>
              ))}
              {!(e.strengths_fr || []).length && <li className="text-xs text-[#8B8B8F] italic">Aucune force marquée.</li>}
            </ul>
          </div>
          <div>
            <div className="text-[11px] font-mono uppercase tracking-widest text-[#DC2626] mb-1.5">À améliorer</div>
            <ul className="space-y-1.5">
              {(e.gaps_fr || []).map((g, i) => (
                <li key={i} className="text-xs text-[#1F2937] flex gap-2"><AlertTriangle className="h-3 w-3 mt-0.5 text-[#DC2626] shrink-0" /><span>{g}</span></li>
              ))}
            </ul>
          </div>
        </div>
        {e.improved_answer_fr && (
          <div className="mt-4 rounded-lg border border-[#C5A880]/40 bg-[#FBF6EB] p-4">
            <div className="text-[11px] font-mono uppercase tracking-widest text-[#7A5B14] mb-1.5">Réponse modèle suggérée</div>
            <div className="text-sm text-[#1F2937] leading-relaxed" data-testid="interview-improved-answer">{e.improved_answer_fr}</div>
          </div>
        )}
      </details>

      <div className="mt-6 flex items-center justify-end gap-2">
        {isLast && allAnswered ? (
          <button
            onClick={onFinish}
            disabled={finishing}
            data-testid="interview-finish-inline-btn"
            className="inline-flex items-center gap-2 rounded-md bg-gradient-to-r from-[#C5A880] to-[#B08948] hover:opacity-90 text-white text-sm font-medium px-5 py-2.5"
          >
            {finishing ? <><Loader2 className="h-4 w-4 animate-spin" /> Rapport…</> : <><Trophy className="h-4 w-4" /> Rapport final</>}
          </button>
        ) : (
          <button
            onClick={onNext}
            data-testid="interview-next-btn"
            className="inline-flex items-center gap-2 rounded-md bg-[#0B2545] hover:bg-[#123163] text-white text-sm font-medium px-5 py-2.5"
          >
            Question suivante <ChevronRight className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );
}

function ReportScreen({ session, onNew }) {
  const r = session.report || {};
  const scores = (session.answers || []).map((a) => a.eval?.score || 0);
  return (
    <div className="max-w-4xl mx-auto" data-testid="interview-report">
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#5B5B62]">Rapport final</div>
          <h1 className="mt-1 text-3xl md:text-4xl font-display text-[#1F2937]">{session.role}</h1>
          <div className="mt-1 text-xs text-[#8B8B8F] font-mono">{session.sector} · {session.difficulty} · {session.answers.length} réponses évaluées</div>
        </div>
        <button
          onClick={onNew}
          data-testid="interview-new-session-btn"
          className="inline-flex items-center gap-2 rounded-md bg-[#0B2545] hover:bg-[#123163] text-white text-sm font-medium px-4 py-2"
        >
          Nouvelle simulation
        </button>
      </div>

      <div className="rounded-2xl border border-[#C5A880]/40 bg-gradient-to-br from-[#FBF6EB] to-white p-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-center">
          <div className="text-center">
            <div className="text-6xl md:text-7xl font-display text-[#7A5B14] leading-none" data-testid="interview-overall-score">{r.overall_score}</div>
            <div className="text-sm text-[#5B5B62] mt-1">/ 100</div>
            <div className="mt-3 inline-flex items-center gap-1 rounded-full bg-white border border-[#C5A880]/40 px-3 py-1">
              <Trophy className="h-3 w-3 text-[#7A5B14]" />
              <span className="font-mono text-[10px] uppercase tracking-widest text-[#7A5B14]">{r.verdict_fr}</span>
            </div>
          </div>
          <div className="md:col-span-2">
            <div className="text-[10px] font-mono uppercase tracking-widest text-[#5B5B62] mb-2">Progression au fil des questions</div>
            <div className="flex items-end gap-1.5 h-16">
              {scores.map((s, i) => (
                <div key={i} className="flex-1 flex flex-col items-center gap-1">
                  <div className="w-full rounded-t bg-gradient-to-t from-[#0B2545] to-[#3C5C8C]" style={{ height: `${s}%` }} title={`Q${i + 1}: ${s}/100`} />
                  <div className="text-[9px] font-mono text-[#8B8B8F]">Q{i + 1}</div>
                </div>
              ))}
            </div>
            {r.encouragement_fr && (
              <div className="mt-4 text-sm italic text-[#1F2937] border-l-2 border-[#C5A880] pl-3">{r.encouragement_fr}</div>
            )}
          </div>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="rounded-xl border border-[#E4E1D8] bg-white p-5">
          <div className="text-[11px] font-mono uppercase tracking-widest text-[#059669] mb-2">Vos forces</div>
          <ul className="space-y-2">
            {(r.top_strengths_fr || []).map((s, i) => (
              <li key={i} className="text-sm text-[#1F2937] flex gap-2"><CheckCircle2 className="h-4 w-4 mt-0.5 text-[#059669] shrink-0" /><span>{s}</span></li>
            ))}
          </ul>
        </div>
        <div className="rounded-xl border border-[#E4E1D8] bg-white p-5">
          <div className="text-[11px] font-mono uppercase tracking-widest text-[#DC2626] mb-2">À travailler en priorité</div>
          <ul className="space-y-2">
            {(r.top_gaps_fr || []).map((g, i) => (
              <li key={i} className="text-sm text-[#1F2937] flex gap-2"><AlertTriangle className="h-4 w-4 mt-0.5 text-[#DC2626] shrink-0" /><span>{g}</span></li>
            ))}
          </ul>
        </div>
      </div>

      <div className="mt-4 rounded-xl border border-[#0B2545]/20 bg-white p-6">
        <div className="text-[11px] font-mono uppercase tracking-widest text-[#0B2545] mb-3">Vos 3 prochaines actions</div>
        <ol className="space-y-3">
          {(r.priority_actions_fr || []).map((a, i) => (
            <li key={i} className="flex gap-3" data-testid={`interview-priority-${i}`}>
              <span className="inline-flex items-center justify-center h-7 w-7 rounded-full bg-[#0B2545] text-white text-xs font-mono shrink-0">{i + 1}</span>
              <div className="flex-1">
                <div className="text-sm font-medium text-[#1F2937]">{a.action}</div>
                <div className="mt-0.5 text-xs text-[#5B5B62]">{a.why}</div>
                <div className="mt-1 inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-[#7A5B14]">
                  <Clock className="h-3 w-3" /> D&apos;ici {a.eta_days} jours
                </div>
              </div>
            </li>
          ))}
        </ol>
      </div>

      <div className="mt-6 text-[10px] font-mono text-[#8B8B8F] text-center">
        Rapport généré par {session.llm_used_report || "modèle interne"} · aide à la décision, jamais une garantie de résultat.
      </div>
    </div>
  );
}

function formatTime(sec) {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

async function openHistoricalSession(id, setSession, setScreen) {
  try {
    const r = await api.get(`/interview/sessions/${id}`);
    const s = r.data.session;
    setSession(s);
    setScreen(s.status === "completed" ? "report" : "live");
  } catch (e) {
    altioraToast.error("Session introuvable");
  }
}
